/**
 * React Query hooks for blog templates with caching.
 *
 * Features:
 * - Automatic caching with 5-minute stale time
 * - Cache invalidation on mutations (create, update, delete, clone)
 * - Optimistic updates where appropriate
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenantStore } from '../stores/useTenantStore';
import {
  blogTemplatesService,
  BlogTemplateSummary,
  BlogTemplate,
  BlogTemplateCreate,
  BlogTemplateUpdate,
  BlogTemplateFilters,
  BlogTemplateCloneRequest,
  ClientTagsResponse,
  SuggestedTemplateResponse,
} from '../services/blogTemplatesService';

// Query key factory for consistent cache keys
export const blogTemplateKeys = {
  all: ['templates'] as const,
  lists: () => [...blogTemplateKeys.all, 'list'] as const,
  list: (tenantId: string, filters?: BlogTemplateFilters) =>
    [...blogTemplateKeys.lists(), tenantId, filters] as const,
  details: () => [...blogTemplateKeys.all, 'detail'] as const,
  detail: (tenantId: string, id: string) =>
    [...blogTemplateKeys.details(), tenantId, id] as const,
  tags: () => [...blogTemplateKeys.all, 'tags'] as const,
  templateTags: (templateId: string) =>
    [...blogTemplateKeys.tags(), templateId] as const,
  suggestion: (tenantId: string, keyword: string, title?: string) =>
    [...blogTemplateKeys.all, 'suggest', tenantId, keyword, title ?? ''] as const,
};

/**
 * Hook to fetch all templates with caching.
 *
 * - 5 minute stale time: won't refetch if data is fresh
 * - 30 minute cache time: keeps data in memory for quick access
 * - Automatic refetch on window focus (after stale time)
 */
export const useBlogTemplates = (filters?: BlogTemplateFilters) => {
  const tenantId = useTenantStore((state) => state.currentTenant);

  return useQuery({
    // Key is inert when tenantId is null — the query is disabled below.
    queryKey: blogTemplateKeys.list(tenantId || '', filters),
    enabled: Boolean(tenantId),
    queryFn: () => blogTemplatesService.list(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes - templates rarely change
    gcTime: 30 * 60 * 1000, // 30 minutes cache (was cacheTime in v4)
    refetchOnWindowFocus: false, // Don't refetch on every tab switch
    // No abort signal — one-time fetch, not a poll. Signal causes ERR_ABORTED
    // during StrictMode double-mount when HTTP/1.1 connection slots are full.
    // The 10s timeout on the service call is sufficient for cleanup.
    retry: 2, // Retry on timeout (browser connection exhaustion after page navigation)
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
  });
};

/**
 * Hook to fetch a single template by ID.
 */
export const useBlogTemplate = (templateId: string | null) => {
  const tenantId = useTenantStore((state) => state.currentTenant);

  return useQuery({
    // Key is inert when tenantId is null — the query is disabled below.
    queryKey: blogTemplateKeys.detail(tenantId || '', templateId || ''),
    queryFn: () => blogTemplatesService.get(templateId!),
    enabled: Boolean(tenantId) && !!templateId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
};

/**
 * Hook for the advisory "suggested template" for a keyword/title.
 * Only fires when a keyword is present; the user's manual pick stays authoritative.
 */
export const useSuggestedTemplate = (keyword: string, title?: string) => {
  const tenantId = useTenantStore((state) => state.currentTenant);
  const kw = keyword.trim();

  return useQuery<SuggestedTemplateResponse>({
    // Key is inert when tenantId is null — the query is disabled below.
    queryKey: blogTemplateKeys.suggestion(tenantId || '', kw, title),
    queryFn: () => blogTemplatesService.suggestTemplate(kw, title),
    enabled: Boolean(tenantId) && !!kw, // only fires when a keyword is present
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
  });
};

/**
 * Hook to create a new template.
 * Invalidates the list cache on success.
 */
export const useCreateBlogTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BlogTemplateCreate) => blogTemplatesService.create(data),
    onSuccess: () => {
      // Invalidate list to show new template
      queryClient.invalidateQueries({ queryKey: blogTemplateKeys.lists() });
    },
  });
};

/**
 * Hook to update a template.
 * Invalidates both list and detail caches on success.
 */
export const useUpdateBlogTemplate = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantStore((state) => state.currentTenant);

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: BlogTemplateUpdate }) => {
      if (!tenantId) throw new Error('No tenant selected');
      return blogTemplatesService.update(id, updates);
    },
    onSuccess: (updatedTemplate) => {
      // Invalidate list cache
      queryClient.invalidateQueries({ queryKey: blogTemplateKeys.lists() });
      // Update detail cache with new data
      queryClient.setQueryData(
        // Non-null: mutationFn above throws when no tenant is selected.
        blogTemplateKeys.detail(tenantId!, updatedTemplate.id),
        updatedTemplate
      );
    },
  });
};

/**
 * Hook to delete a template.
 * Invalidates list cache and removes detail from cache.
 */
export const useDeleteBlogTemplate = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantStore((state) => state.currentTenant);

  return useMutation({
    mutationFn: (templateId: string) => {
      if (!tenantId) throw new Error('No tenant selected');
      return blogTemplatesService.delete(templateId);
    },
    onSuccess: (_data, templateId) => {
      // Invalidate list cache
      queryClient.invalidateQueries({ queryKey: blogTemplateKeys.lists() });
      // Remove from detail cache
      // Non-null: mutationFn above throws when no tenant is selected.
      queryClient.removeQueries({ queryKey: blogTemplateKeys.detail(tenantId!, templateId) });
    },
  });
};

/**
 * Hook to clone/customize a template.
 * Invalidates list cache to show the new cloned template.
 */
export const useCloneBlogTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sourceTemplateId,
      request,
    }: {
      sourceTemplateId: string;
      request: BlogTemplateCloneRequest;
    }) => blogTemplatesService.clone(sourceTemplateId, request),
    onSuccess: () => {
      // Invalidate list to show cloned template
      queryClient.invalidateQueries({ queryKey: blogTemplateKeys.lists() });
    },
  });
};

/**
 * Hook to prefetch templates (useful for navigation)
 */
export const usePrefetchBlogTemplates = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantStore((state) => state.currentTenant);

  return () => {
    if (!tenantId) return; // best-effort prefetch — nothing to prefetch without a client
    queryClient.prefetchQuery({
      queryKey: blogTemplateKeys.list(tenantId),
      queryFn: () => blogTemplatesService.list(),
      staleTime: 5 * 60 * 1000,
    });
  };
};

// =============================================================================
// Tag Management Hooks
// =============================================================================

/**
 * Hook to fetch client tags for a specific template.
 */
export const useBlogTemplateTags = (templateId: string | null) => {
  return useQuery({
    queryKey: blogTemplateKeys.templateTags(templateId || ''),
    queryFn: () => blogTemplatesService.getTags(templateId!),
    enabled: !!templateId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

/**
 * Hook to toggle a tag for the current tenant on a template.
 * Invalidates both tags cache and list cache (since filtered views may change).
 */
export const useToggleBlogTemplateTag = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantStore((state) => state.currentTenant);

  return useMutation({
    mutationFn: ({
      templateId,
      isCurrentlyTagged,
    }: {
      templateId: string;
      isCurrentlyTagged: boolean;
    }) => {
      if (!tenantId) throw new Error('No tenant selected');
      return blogTemplatesService.toggleTag(templateId, tenantId, isCurrentlyTagged);
    },
    onSuccess: (result, { templateId }) => {
      // Update the specific template's tags cache
      queryClient.setQueryData(blogTemplateKeys.templateTags(templateId), result);
      // Invalidate list queries since "My Templates" filter may change
      queryClient.invalidateQueries({ queryKey: blogTemplateKeys.lists() });
    },
  });
};

/**
 * Hook to add a tag to a template for the current tenant.
 */
export const useAddBlogTemplateTag = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantStore((state) => state.currentTenant);

  return useMutation({
    mutationFn: (templateId: string) => {
      if (!tenantId) throw new Error('No tenant selected');
      return blogTemplatesService.addTag(templateId, tenantId);
    },
    onSuccess: (result, templateId) => {
      queryClient.setQueryData(blogTemplateKeys.templateTags(templateId), result);
      queryClient.invalidateQueries({ queryKey: blogTemplateKeys.lists() });
    },
  });
};

/**
 * Hook to remove a tag from a template for the current tenant.
 */
export const useRemoveBlogTemplateTag = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantStore((state) => state.currentTenant);

  return useMutation({
    mutationFn: (templateId: string) => {
      if (!tenantId) throw new Error('No tenant selected');
      return blogTemplatesService.removeTag(templateId, tenantId);
    },
    onSuccess: (result, templateId) => {
      queryClient.setQueryData(blogTemplateKeys.templateTags(templateId), result);
      queryClient.invalidateQueries({ queryKey: blogTemplateKeys.lists() });
    },
  });
};

/**
 * Hook to fetch recommended templates (R5 ruled source: list_recommended
 * endpoint). Returns BlogTemplateSummary[] — consumers map these to IDs and
 * pass them to RecipeChooser as `recommendedIds`.
 *
 * Endpoint-resilience contract (ruled, do not remove): when this query is
 * loading or errors, consumers pass `undefined` and the chooser's client-side
 * heuristic (!gate_conflict && is_system) remains the explicit fallback.
 */
export const useRecommendedTemplates = (limit: number = 3) => {
  const tenantId = useTenantStore((state) => state.currentTenant);

  return useQuery({
    // Key is inert when tenantId is null — the query is disabled below.
    queryKey: [...blogTemplateKeys.lists(), tenantId || '', 'recommended', { limit }] as const,
    enabled: Boolean(tenantId),
    queryFn: () => blogTemplatesService.getRecommended('universal', limit),
    staleTime: 5 * 60 * 1000, // 5 minutes - recommendations rarely change
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
  });
};
